import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import Header from "../../Components/Header/Header";
import PageHeadings from "../../Components/PageHeadings/PageHeadings";
import FiltersSidebar from "../../Components/FiltersSidebar/FiltersSidebar";
import DashboardsCards from "../../Components/DashboardCards/DashboardsCards";
import Footer from "../../Components/Footer/Footer";
import {
  DEFAULT_SPORT,
  PAGE_SIZE,
  SPORT_NAMES,
  playerUrl,
} from "../../Utils/Constants";
import { errorMessage, filterPlayers } from "../../Utils/api";

/**
 * Browse athletes.
 *
 * Filter state lives in the querystring, not component state. Previously it was
 * `useState`, which meant a filtered view could not be shared or bookmarked,
 * Back from an athlete lost the filters, and a refresh reset everything.
 */
const Dashboard = ({ mode = "both" }) => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [players, setPlayers] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [apiCalled, setApiCalled] = useState(false);
  const [open, setOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const showContribute = mode !== "collab";
  const showCollab = mode !== "contribute";

  // ── URL -> filter state ─────────────────────────────────────────────────
  const asOption = (value, label) =>
    value ? { value, label: label ?? String(value) } : "";

  const filterState = useMemo(() => {
    const sportId = Number(params.get("sport")) || DEFAULT_SPORT.value;
    return {
      sport: {
        value: sportId,
        label: SPORT_NAMES[sportId] || DEFAULT_SPORT.label,
      },
      conference: asOption(params.get("conference")),
      team: asOption(
        Number(params.get("team")) || "",
        params.get("team_name") || ""
      ),
      class: asOption(params.get("class")),
      position: asOption(params.get("position")),
    };
  }, [params]);

  const playerFilter = params.get("q") || "";
  const skip = Number(params.get("skip")) || 0;

  // ── filter state -> URL ─────────────────────────────────────────────────
  const setFilterState = useCallback(
    (next) => {
      setParams(
        (prev) => {
          const current = {
            sport: { value: Number(prev.get("sport")) || DEFAULT_SPORT.value },
            conference: asOption(prev.get("conference")),
            team: asOption(Number(prev.get("team")) || "", prev.get("team_name") || ""),
            class: asOption(prev.get("class")),
            position: asOption(prev.get("position")),
          };
          const value = typeof next === "function" ? next(current) : next;

          const out = new URLSearchParams();
          if (prev.get("q")) out.set("q", prev.get("q"));
          if (value.sport?.value) out.set("sport", String(value.sport.value));
          if (value.conference?.value) out.set("conference", value.conference.value);
          if (value.team?.value) {
            out.set("team", String(value.team.value));
            if (value.team.label) out.set("team_name", value.team.label);
          }
          if (value.class?.value) out.set("class", value.class.value);
          if (value.position?.value) out.set("position", value.position.value);
          // Any filter change returns to the first page.
          return out;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const setPlayerFilter = useCallback(
    (text) => {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          if (text) out.set("q", text);
          else out.delete("q");
          out.delete("skip");
          return out;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  // ── Fetch ───────────────────────────────────────────────────────────────
  const body = useMemo(() => {
    const payload = {};
    if (filterState.sport?.value) payload.sport_id = filterState.sport.value;
    if (filterState.team?.value) payload.team_id = Number(filterState.team.value);
    if (filterState.conference?.value) payload.conference = filterState.conference.value;
    if (filterState.class?.value) payload.player_class = filterState.class.value;
    if (filterState.position?.value) payload.position = filterState.position.value;
    if (playerFilter.trim()) payload.player_name = playerFilter.trim();
    return payload;
  }, [filterState, playerFilter]);

  const bodyKey = JSON.stringify(body);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    // Debounced, so typing in the search box does not fire a request per key.
    const timer = setTimeout(() => {
      filterPlayers(body, { limit: PAGE_SIZE, skip })
        .then((data) => {
          if (cancelled) return;
          const page = data.filtered_players || [];
          setCount(data.count || 0);
          setPlayers((prev) => (skip === 0 ? page : [...prev, ...page]));
        })
        .catch((error) => {
          if (!cancelled) toast.error(errorMessage(error, "Failed to load athletes"));
        })
        .finally(() => {
          if (cancelled) return;
          setApiCalled(true);
          setLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyKey, skip]);

  const handleNextClick = () => {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.set("skip", String(players.length));
        return out;
      },
      { replace: true }
    );
  };

  // The debounced effect already refetches on change; this just collapses the
  // delay when the user explicitly hits search.
  const handleSearchPlayers = () => {
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.delete("skip");
        return out;
      },
      { replace: true }
    );
  };

  // In-page navigation. These were window.open(..., "_blank"), which broke the
  // Back button, lost scroll position on mobile, and got silently blocked by
  // Safari's popup heuristics.
  const handleViewDetail = (player) => navigate(playerUrl(player));
  const handleViewFanEngagement = (player) =>
    navigate(`/FanExperience/${player.player_id}/${player.team_id}?mode=${mode}`);
  const handleViewBrandEngagement = (player) =>
    navigate(`/BrandPartnership/${player.player_id}/${player.team_id}?mode=${mode}`);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const filters = (
    <FiltersSidebar
      loading={loading}
      filterState={filterState}
      setFilterState={setFilterState}
      playerFilter={playerFilter}
      setPlayerFilter={setPlayerFilter}
      handleSearchPlayers={handleSearchPlayers}
      resultCount={count}
    />
  );

  return (
    <div>
      <Header />
      <div className="page-main-container">
        <PageHeadings showContribute={showContribute} />
        <div className="filter-sidebar-plus-dashcards">
          {isMobile && (
            <button
              onClick={() => setShowFilters((prev) => !prev)}
              className="show-filter-btn-dash"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          )}
          {isMobile ? (showFilters ? filters : null) : filters}
          <DashboardsCards
            handleViewDetail={handleViewDetail}
            handleViewFanEngagement={handleViewFanEngagement}
            handleViewBrandEngagement={handleViewBrandEngagement}
            playersCount={count}
            players={players}
            loading={loading}
            setLoading={setLoading}
            handleNextClick={handleNextClick}
            isFiltered
            open={open}
            setOpen={setOpen}
            apiCalled={apiCalled}
            showContribute={showContribute}
            showCollab={showCollab}
            mode={mode}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Dashboard;
